import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import Layout from '../components/Layout';
import { listReports, type ReportListItem } from '../services/reportService';

/** Human labels for the report genres (backend `reports.genre`). */
const GENRE_LABEL: Record<string, string> = {
  segmentation: 'Segmentation',
  a_and_u: 'Attitudes & Usage',
  customer_profile: 'Customer Profile',
  concept_test: 'Concept Test',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function ReportsList() {
  const [reports, setReports] = useState<ReportListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listReports()
      .then((r) => active && setReports(r))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <header className="mb-10">
          <h1 className="text-3xl font-semibold text-[#0E3B37]">Reports</h1>
          <p className="text-slate-500 mt-1">
            Interactive reports built from real consumer research.
          </p>
        </header>

        {error && <div className="text-red-600">Failed to load reports: {error}</div>}
        {!error && !reports && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading reports…</p>
          </div>
        )}
        {reports && reports.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            No reports yet. Generated reports will appear here.
          </div>
        )}

        {reports && reports.length > 0 && (
          <ul className="space-y-3">
            {reports.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/reports/${r.id}`}
                  className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 hover:border-[#14B8A6] hover:shadow-sm transition-all"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF1EE] text-[#14B8A6]">
                    <FileText size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#0A2A28] truncate">{r.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {GENRE_LABEL[r.genre] ?? r.genre} · Updated {formatDate(r.updated_at)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
